import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { MoodTest } from "../features/diary/mood-form/mood-form.component";
import { AsthmaObservation, AsthmaTest, ChronicDisease, ChronicDiseaseObservation } from "../models/asthma-test.model";
import { DeleteObservationItem, ObservationData } from "../models/observation.model";
import { Conditions, FetchClassificatorsService, ObservationTypes, UnitValues } from "./fetch-classificators.service";
import { AuditTest } from "../features/diary/audit-form/audit-form.component";
import { SessionContext } from "../models/session-context.model";
import { UserInfoService } from "./user-info.service";
import { TranslationService } from "./translation-service";
import { Observation } from "../models/payloads/shared/save-request.model";


@Injectable({
  providedIn: "root",
})
export class DataTransformService {

  feelingClassificator = [
    "FEELING_1",
    "FEELING_2",
    "FEELING_3",
  ];

  conditionClassificator = [
    "EEK2_EEK2_GRP1",
    "EEK2_EEK2_GRP2",
    "EEK2_EEK2_GRP3",
    "EEK2_EEK2_GRP4",
    "EEK2_EEK2_GRP5",
    "EEK2_EEK2_GRP6",
  ];

  conditions: Conditions;
  unitValues: UnitValues;
  observationTypes: ObservationTypes = new ObservationTypes();
  userData: SessionContext;

  constructor(private translate: TranslateService,
              private classificatorService: FetchClassificatorsService,
              private userService: UserInfoService,
              private translation: TranslationService) {
    this.classificatorService.hasClassificators.subscribe((data) => {
      if (data == true) {
        this.userService.sessionContext.subscribe((data) => this.userData = data);
        this.classificatorService.conditions.subscribe((data) => this.conditions = data);
        this.classificatorService.observationTypes.subscribe((data) => this.observationTypes = data);
        this.classificatorService.unitValues.subscribe((data) => this.unitValues = data);
      }
    });
  }

  createAsthmaTestString(asthmaTest: AsthmaTest): string {
    return this.translation.translateTextEst("asthmaTest.summary1") +
      asthmaTest.date.toLocaleDateString("et-EE") +
      this.translation.translateTextEst("asthmaTest.summary2") +
      asthmaTest.total;
  }

  createAsthmaTestObservation(asthmaTest: AsthmaTest): Observation {
    const classificatorlist = [
      "AKT_AKT_GRP",
      "AKT_1",
      "AKT_2",
      "AKT_3",
      "AKT_4",
      "AKT_5",
    ];
    let observation: Observation = null;
    for (let i = 0; i < classificatorlist.length; i++) {
      const classificatorCode = JSON.parse(
        sessionStorage.getItem(classificatorlist[i]));
      if (i === 0) {
        observation = new Observation(classificatorCode, [], asthmaTest.total);
      } else {
        const answer: Observation = new Observation(classificatorCode, null, asthmaTest.scores[i - 1]);
        observation.observationList.push(answer);
      }
    }
    return observation;
  }

  responseToAsthmaTestArray(items: AsthmaObservation[]): AsthmaTest[] {
    const asthmaTestArray: AsthmaTest[] = [];
    for (let i = 0; i < items.length; i++) {
      const date = new Date(items[i].insertTime);
      if (!items[i].observationList || !items[i].observationList.length) {
        return;
      }
      const total = parseInt(items[i].observationList[0].value);
      const id = items[i].id;
      let scoreCategory: string;
      if (total > 24) {
        scoreCategory = "excellent";
      } else {
        scoreCategory = total > 19 ? "good" : "poor";
      }
      const asthmaTest: AsthmaTest = {
        date,
        total,
        scores: [],
        scoreCategory,
        originalObservationId: id,
      };
      if (items[i].observationList[0].observationList) {
        for (let j = 0; j < items[i].observationList[0].observationList.length; j++) {
          const questionScore = items[i].observationList[0].observationList[j].value;
          asthmaTest.scores.push(parseInt(questionScore));
        }
      }
      asthmaTestArray.push(asthmaTest);
    }
    return asthmaTestArray;
  }

  createMoodTestObservationList(moodTest: MoodTest): Observation[] {
    const classificatorList = [
      "EEK2_EEK2_GRP1",
      "EEK2_EEK2_GRP2",
      "EEK2_EEK2_GRP3",
      "EEK2_EEK2_GRP4",
      "EEK2_EEK2_GRP5",
      "EEK2_EEK2_GRP6",
    ];
    const observationList: Observation[] = [];
    for (let i = 0; i < classificatorList.length; i++) {
      const scaleCode = JSON.parse(
        sessionStorage.getItem(classificatorList[i]));
      const scaleObservation: Observation = new Observation(scaleCode, [], moodTest.scales[i].total);
      for (let j = 0; j < moodTest.scales[i].nrOfQuestions; j++) {
        const questionCode: number = JSON.parse(
          sessionStorage.getItem("EEK2_" + (j + 1)),
        );
        const score = !!moodTest.scales[i].scores[j]
          ? moodTest.scales[i].scores[j]
          : 0;
        const questionObservation: Observation = new Observation(questionCode, null, score);
        scaleObservation.observationList.push(questionObservation);
      }
      observationList.push(scaleObservation);
    }
    return observationList;
  }


  createAuditTestObservationList(auditTest: AuditTest[], overallScore): Observation {
    const classificatorList = [
      "AUDIT_AUDIT_GRP",
      "AUDIT_1",
      "AUDIT_2",
      "AUDIT_3",
      "AUDIT_4",
      "AUDIT_5",
      "AUDIT_6",
      "AUDIT_7",
      "AUDIT_8",
      "AUDIT_9",
      "AUDIT_10",

    ];
    let observation: Observation = null;
    for (let i = 0; i < classificatorList.length; i++) {
      const classificatorCode = JSON.parse(
        sessionStorage.getItem(classificatorList[i]));
      if (i === 0) {
        observation = new Observation(classificatorCode, [], overallScore);
      } else {
        const score = !!auditTest[i - 1].answer ? auditTest[i - 1].answer : 0;
        const answer: Observation = new Observation(classificatorCode, null, score);
        observation.observationList.push(answer);
      }
    }
    return observation;
  }

  createMoodTestString(moodTest: MoodTest): string {
    let moodTestString: string = this.translate.instant("moodForm.summary");
    for (let i = 0; i < moodTest.scales.length; i++) {
      moodTestString +=
        "\r\n" +
        this.translate.instant("moodForm." + moodTest.scales[i].type) +
        " = " +
        moodTest.scales[i].total;
    }
    return moodTestString;
  }

  responseToDiseaseArray(items: ChronicDiseaseObservation[]): ChronicDisease[] {
    const chronicDiseaseArray: ChronicDisease[] = [];
    for (let i = 0; i < items.length; i++) {
      const date = new Date(items[i].insertTime);
      const key = Object.keys(sessionStorage).find(key => sessionStorage[key] == items[i].classificatorCodeId);
      const chronicDisease: ChronicDisease = {
        dateAssigned: date,
        diagnosis: items[i].classificatorCode.name,
        assignedBy: items[i].representerPerson.firstName.toLowerCase() + " " + items[i].representerPerson.lastName.toLowerCase(),
        diseaseKey: key.toLowerCase(),
      };
      chronicDiseaseArray.push(chronicDisease);
    }
    return chronicDiseaseArray;
  }

  /**
   * To delete observation and it's children entry pass DELETE mode where id = observation masterId
   */
  deleteObservation(oldPressure: ObservationData) {
    let id = oldPressure.masterObservationId ? oldPressure.masterObservationId : oldPressure.id;
    return new DeleteObservationItem(id);
  }

  createBloodPressureObservation(newObs: ObservationData, systolicValue: number, systolicUnit: number, diastolicValue: number, diastolicUnit: number, bpmValue: number, bpmUnit: number) {
    let childObservations: ObservationData[] = [];
    let master = new ObservationData();
    master.classificatorCodeId = this.observationTypes.bloodPressure;
    master.effectiveTime = newObs.effectiveTime;
    master.conditionCodeId = newObs.conditionCodeId;
    master.deviceId = newObs.deviceId;
    master.feelingCodeId = newObs.feelingCodeId;
    master.observationList = childObservations;
    master.personId = this.userData.principal.id;
    master.representerPersonId = this.userData.representerPerson.id;


    let systolicObs = new ObservationData();
    systolicObs.classificatorCodeId = this.observationTypes.systolic;
    systolicObs.effectiveTime = newObs.effectiveTime;
    systolicObs.value = systolicValue.toString();
    systolicObs.valueUnitId = systolicUnit;
    systolicObs.valueTypeId = newObs.valueTypeId;
    systolicObs.personId = this.userData.principal.id;
    systolicObs.representerPersonId = this.userData.representerPerson.id;


    let diastolicObs = new ObservationData();
    diastolicObs.classificatorCodeId = this.observationTypes.diastolic;
    diastolicObs.effectiveTime = newObs.effectiveTime;
    diastolicObs.value = diastolicValue.toString();
    diastolicObs.valueUnitId = diastolicUnit;
    diastolicObs.valueTypeId = newObs.valueTypeId;
    diastolicObs.personId = this.userData.principal.id;
    diastolicObs.representerPersonId = this.userData.representerPerson.id;


    let pulseObs = new ObservationData();
    pulseObs.classificatorCodeId = this.observationTypes.pulse;
    pulseObs.effectiveTime = newObs.effectiveTime;
    pulseObs.value = bpmValue.toString();
    pulseObs.valueUnitId = bpmUnit;
    pulseObs.valueTypeId = newObs.valueTypeId;
    pulseObs.personId = this.userData.principal.id;
    pulseObs.representerPersonId = this.userData.representerPerson.id;


    childObservations.push(systolicObs);
    childObservations.push(diastolicObs);
    childObservations.push(pulseObs);

    console.log("new blood pressure obs: " + JSON.stringify(master));
    return master;
  }

  createNewBloodSugarObservation(newObs: ObservationData) {
    let childObservations: ObservationData[] = [];
    let master = new ObservationData();
    master.classificatorCodeId = this.observationTypes.bloodSugar;
    master.effectiveTime = newObs.effectiveTime;
    master.conditionCodeId = newObs.conditionCodeId;
    master.deviceId = newObs.deviceId;
    master.feelingCodeId = newObs.feelingCodeId;
    master.observationList = childObservations;
    master.personId = this.userData.principal.id;
    master.representerPersonId = this.userData.representerPerson.id;

    let bloodSugar = new ObservationData();
    bloodSugar.classificatorCodeId = this.observationTypes.bloodSugar;
    bloodSugar.effectiveTime = newObs.effectiveTime;
    bloodSugar.value = newObs.value;
    bloodSugar.valueUnitId = newObs.valueUnitId;
    bloodSugar.valueTypeId = newObs.valueTypeId;
    bloodSugar.personId = this.userData.principal.id;
    bloodSugar.representerPersonId = this.userData.representerPerson.id;

    childObservations.push(bloodSugar);
    console.log(master);
    return master;
  }

  createNewPefObservation(newObs: ObservationData) {
    let childObservations: ObservationData[] = [];
    let master = new ObservationData();
    master.classificatorCodeId = this.observationTypes.PEF;
    master.effectiveTime = newObs.effectiveTime;
    master.conditionCodeId = newObs.conditionCodeId;
    master.deviceId = newObs.deviceId;
    master.feelingCodeId = newObs.feelingCodeId;
    master.observationList = childObservations;
    master.personId = this.userData.principal.id;
    master.representerPersonId = this.userData.representerPerson.id;

    let pef = new ObservationData();
    pef.classificatorCodeId = this.observationTypes.PEF;
    pef.effectiveTime = newObs.effectiveTime;
    pef.value = newObs.value;
    pef.valueUnitId = newObs.valueUnitId;
    pef.valueTypeId = newObs.valueTypeId;
    pef.personId = this.userData.principal.id;
    pef.representerPersonId = this.userData.representerPerson.id;

    childObservations.push(pef);
    console.log(master);
    return master;
  }

  createNewMeasurementObservation(newObs: ObservationData, weightUnitId: number, weightValue: number, heightUnitId: number, heightValue: number, waistUnitId: number, waistValue: number) {
    let childObservations: ObservationData[] = [];
    let master = new ObservationData();
    master.classificatorCodeId = this.observationTypes.weight;
    master.effectiveTime = newObs.effectiveTime;
    master.conditionCodeId = newObs.conditionCodeId;
    master.feelingCodeId = newObs.feelingCodeId;
    master.personId = this.userData.principal.id;
    master.representerPersonId = this.userData.representerPerson.id;
    master.observationList = childObservations;

    let weightObs = new ObservationData();
    weightObs.classificatorCodeId = this.observationTypes.weight;
    weightObs.effectiveTime = newObs.effectiveTime;
    weightObs.value = weightValue.toString();
    weightObs.valueUnitId = weightUnitId;
    weightObs.valueTypeId = newObs.valueTypeId;
    weightObs.personId = this.userData.principal.id;
    weightObs.representerPersonId = this.userData.representerPerson.id;

    let heightObs = new ObservationData();
    if (heightValue && heightUnitId) {
      heightObs.classificatorCodeId = this.observationTypes.height;
      heightObs.effectiveTime = newObs.effectiveTime;
      heightObs.value = heightValue.toString();
      heightObs.valueUnitId = heightUnitId;
      heightObs.valueTypeId = newObs.valueTypeId;
      heightObs.personId = this.userData.principal.id;
      heightObs.representerPersonId = this.userData.representerPerson.id;
    }

    let waistObs = new ObservationData();
    if (waistValue && waistUnitId) {
      waistObs.classificatorCodeId = this.observationTypes.waist;
      waistObs.effectiveTime = newObs.effectiveTime;
      waistObs.value = waistValue.toString();
      waistObs.valueUnitId = waistUnitId;
      waistObs.valueTypeId = newObs.valueTypeId;
      waistObs.personId = this.userData.principal.id;
      waistObs.representerPersonId = this.userData.representerPerson.id;
    }

    let bmiObs = new ObservationData();
    if (heightValue && weightValue) {
      bmiObs.classificatorCodeId = this.observationTypes.BMI;
      bmiObs.effectiveTime = newObs.effectiveTime;
      bmiObs.value = this.round((weightValue / (Math.pow(heightValue, 2)) * 10000), 1).toString();
      bmiObs.valueUnitId = this.unitValues.kg_m2;
      bmiObs.valueTypeId = newObs.valueTypeId;
      bmiObs.personId = this.userData.principal.id;
      bmiObs.representerPersonId = this.userData.representerPerson.id;
    }

    childObservations.push(weightObs);

    if (heightObs.value) {
      childObservations.push(heightObs);
    }
    if (waistObs.value) {
      childObservations.push(waistObs);
    }
    if (bmiObs.value) {
      childObservations.push(bmiObs);
    }


    console.log("new measurement obs: " + JSON.stringify(master));
    return master;
  }


  /**
   * @value numeric float number
   * @precision to round to
   * round(12345.6789) // 12346
   * round(12345.6789, 2) // 12345.68
   * round(12345.6789, 1) // 12345.7
   * round(456.7, 2).toFixed(2) // "456.70"
   */
  round(value, precision) {
    const multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
  }
}

